import clsx from "clsx";
import { Crosshair } from "lucide-react";
import { type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { designate } from "../components/attention";
import { Motion, Presence } from "../components/presence";
import { t } from "../i18n/i18n";
import { formatMgrs } from "../map/MapCanvas";
import { mapDropTarget } from "../map/units/listDrag";
import {
  createListDrag,
  type DragItem,
  ghostAfterEnd,
  type GhostFrame,
  type Refusal,
  refusalNotice,
} from "../map/units/listDragGesture";
import { MAP_EXIT_MS } from "../map/units/phaseTransition";
import { symbolSvg } from "../map/units/symbols";
import { useUi } from "../state/ui";

/** Sélecteur du bouton « Cadrer sur le camp » de la barre de la carte. */
const FIT_BUTTON = '[data-attention="map-fit"]';

/** Zone d'une liste glissable (`data-list-drag-zone`) : y relâcher l'unité abandonne le geste sans avis. */
export const LIST_DRAG_ZONE = { "data-list-drag-zone": "" } as const;

/** Glisser une unité d'une liste vers la carte (docs/14 §2.6) : branche le geste (listDragGesture.ts) sur les
 *  lignes (`bind(item)`) et dessine le fantôme (`layer`, à rendre une fois). Le fantôme (symbole et nom) suit le
 *  pointeur ; au-dessus de la carte, un réticule donne la coordonnée MGRS sous le pointeur, en ton d'avertissement
 *  hors de l'emprise. Dépôt accepté : `drop`. Dépôt refusé ou geste annulé : le fantôme revient à sa ligne en fondu,
 *  et l'avis désigne l'action attendue (`refusalNotice`). */
export function useListDrag<I extends DragItem>(options: {
  canEdit: boolean;
  refuseNotSaved: () => void;
  drop: (item: I, at: [number, number]) => void;
  /** Simple clic à la souris sur une ligne glissable (sans glisser). */
  click?: (row: HTMLElement, item: I) => void;
}): {
  bind: (item: I) => {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
    onClickCapture: (event: ReactMouseEvent<HTMLElement>) => void;
  };
  /** Unité en cours de glissé (son `id`), ou null. */
  dragging: string | null;
  layer: ReactNode;
} {
  const [ghost, setGhost] = useState<GhostFrame | null>(null);
  const latest = useRef(options);
  latest.current = options;
  const ghostRef = useRef<HTMLDivElement>(null);
  // Animation de retour du fantôme : annulée au geste suivant (sinon son `fill` laisserait le fantôme invisible).
  const back = useRef<Animation | null>(null);

  const gesture = useMemo(() => {
    const stopBack = () => {
      back.current?.cancel();
      back.current = null;
    };
    const sendBack = (row: HTMLElement, id: number) => {
      const finish = () => setGhost((g) => ghostAfterEnd(g, id));
      const element = ghostRef.current;
      stopBack();
      if (!element || typeof element.animate !== "function") return finish();
      const box = row.getBoundingClientRect();
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      const from = element.style.transform;
      const to = `translate(${box.left + 12}px, ${box.top + box.height / 2}px)`;
      const animation = element.animate(
        reduced ? [{ opacity: 1 }, { opacity: 0 }] : [{ transform: from, opacity: 1 }, { transform: to, opacity: 0 }],
        { duration: MAP_EXIT_MS, easing: "ease-in", fill: "forwards" },
      );
      back.current = animation;
      animation.finished.then(finish, finish).finally(() => {
        if (back.current === animation) back.current = null;
      });
    };
    const refuse = (row: HTMLElement, refusal: Refusal | null, at: { x: number; y: number } | null) => {
      const map = mapDropTarget();
      const zone = row.closest("[data-list-drag-zone]");
      const under = at ? document.elementFromPoint(at.x, at.y) : null;
      const fit = document.querySelector<HTMLButtonElement>(FIT_BUTTON);
      const notice = refusalNotice(refusal, {
        overSource: Boolean(zone && under && zone.contains(under)),
        free: map?.freeZone() ?? false,
        canFit: Boolean(map && fit && !fit.disabled),
      });
      const notify = useUi.getState().notify;
      if (notice?.kind === "map") {
        notify({
          text: t("Unité non posée"),
          detail: t("Relâchez l'unité au-dessus de la carte pour la poser."),
          tone: "info",
        });
      } else if (notice?.kind === "zone") {
        // Action attendue : déposer dans l'emprise du dossier ; « Cadrer sur le camp » (ou la zone) la ramène à l'écran.
        if (notice.designateFit) designate(FIT_BUTTON);
        notify({
          text: notice.free ? t("Hors du terrain") : t("Hors du camp"),
          detail: notice.free
            ? t("Déposez l'unité sur la carte, dans l'emprise du terrain.")
            : t("Déposez l'unité sur la carte, dans l'emprise du camp."),
          tone: "warn",
          action:
            notice.designateFit && map
              ? { label: notice.free ? t("Cadrer sur le terrain") : t("Cadrer sur le camp"), run: () => map.fit() }
              : undefined,
        });
      }
    };
    return createListDrag<I, HTMLElement>({
      map: mapDropTarget,
      canEdit: () => latest.current.canEdit,
      refuseNotSaved: () => latest.current.refuseNotSaved(),
      ghost: (frame) => {
        setGhost((g) => {
          if (!g || g.gesture !== frame.gesture) stopBack();
          return frame;
        });
      },
      drop: (item, at, id) => {
        stopBack();
        setGhost((g) => ghostAfterEnd(g, id));
        latest.current.drop(item, at);
      },
      back: (row, id, refusal, at) => {
        sendBack(row, id);
        refuse(row, refusal, at);
      },
      click: (row, item) => latest.current.click?.(row, item),
    });
  }, []);

  const bind = (item: I) => ({
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
      gesture.down(event, event.currentTarget, item);
    },
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => {
      if (gesture.move(event)) event.preventDefault();
    },
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => gesture.up(event),
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => gesture.cancel(event),
    // Le clic qui suit un glissé ne localise pas l'unité (bouton de la ligne).
    onClickCapture: (event: ReactMouseEvent<HTMLElement>) => {
      if (!gesture.swallowClick()) return;
      event.preventDefault();
      event.stopPropagation();
    },
  });

  const layer =
    typeof document === "undefined"
      ? null
      : createPortal(
          <Presence show={ghost !== null}>
            {ghost && (
              <div
                // Un élément par geste : rien ne reste de l'animation de retour du geste précédent.
                key={ghost.gesture}
                ref={ghostRef}
                className="pointer-events-none fixed top-0 left-0 z-30"
                style={{ transform: `translate(${ghost.x}px, ${ghost.y}px)` }}
                aria-hidden
              >
                {/* Réticule au-dessus de la carte : centré sur le pointeur, en ton d'avertissement hors de l'emprise. */}
                <Presence show={ghost.over !== null}>
                  <Motion origin="fade" className={clsx("absolute -top-3 -left-3", ghost.inside ? "text-ink" : "text-warn")}>
                    <Crosshair size={24} strokeWidth={1.5} />
                  </Motion>
                </Presence>
                <Motion
                  origin="zoom"
                  className="mt-4 ml-4 flex w-max items-center gap-1.5 rounded-lg border border-line bg-surface px-2 py-1 shadow-float"
                >
                  <span
                    className="flex h-4 w-6 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
                    dangerouslySetInnerHTML={{ __html: symbolSvg(ghost.sidc, 13) }}
                  />
                  <span className="font-mono text-ui font-semibold text-ink tabular-nums">{ghost.label}</span>
                  {ghost.over && (
                    <span className={clsx("font-mono text-meta tabular-nums", ghost.inside ? "text-ink-muted" : "text-warn")}>
                      {ghost.inside ? formatMgrs(ghost.over[0], ghost.over[1], 3) : t("Hors de l'emprise")}
                    </span>
                  )}
                </Motion>
              </div>
            )}
          </Presence>,
          document.body,
        );

  return { bind, dragging: ghost?.id ?? null, layer };
}
